import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Sparkles, TrendingUp, TrendingDown, AlertTriangle, MapPin, Clock,
  UserX, Mail, Zap, Calendar, MessageSquare, ArrowLeft, Loader2,
  RefreshCw, DollarSign,
} from "lucide-react";
import { isEmbedded } from "@/lib/tenant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Get current week's Monday (ISO). */
function currentMonday() {
  const d = new Date();
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}

export default function AIInsightsPage() {
  const [week, setWeek] = useState(currentMonday());
  const [tab, setTab] = useState("digest");
  const [tenant, setTenant] = useState({ name: "Market Place on Oakton" });

  useEffect(() => {
    axios.get(`${API}/tenant/current`).then((r) => {
      if (r.data?.exists) setTenant({ name: r.data.name });
    }).catch(() => {});
  }, []);

  const tabs = [
    { id: "digest",    label: "Sunday Digest", icon: Sparkles },
    { id: "pnl",       label: "Dept P&L",       icon: DollarSign },
    { id: "ot",        label: "OT Forecast",    icon: Clock },
    { id: "noshow",    label: "No-Show Risk",   icon: UserX },
    { id: "location",  label: "Location Flags", icon: MapPin },
    { id: "optimizer", label: "Labor Optimizer",icon: Zap },
    { id: "forecast",  label: "Revenue Forecast", icon: TrendingUp },
    { id: "ask",       label: "Ask AI",         icon: MessageSquare },
    { id: "auto",      label: "Auto-Schedule",  icon: Calendar },
    { id: "buddy",     label: "Buddy-Punch",    icon: AlertTriangle },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F1013] via-[#1A1620] to-[#251320]">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#0B0D12] to-[#1A1A2E] border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          {isEmbedded() ? <div className="w-6" /> : (
            <Link to="/timecard" className="text-white/70 hover:text-white flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          )}
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold">
              Powered by AV Tech · Gemini 3
            </div>
            <h1 className="text-white font-black text-lg flex items-center gap-2 justify-center">
              <Sparkles className="w-5 h-5 text-amber-300" /> AI Business Insights
            </h1>
          </div>
          <input
            type="date"
            value={week}
            onChange={(e) => setWeek(e.target.value)}
            className="bg-white/10 text-white border border-white/20 rounded-md px-2 py-1 text-xs"
            data-testid="ai-week-picker"
          />
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-2 pb-2 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 whitespace-nowrap transition ${
                    tab === t.id
                      ? "bg-amber-400 text-black"
                      : "text-white/70 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {tab === "digest"    && <SundayDigest week={week} tenant={tenant} />}
        {tab === "pnl"       && <DeptPnL week={week} />}
        {tab === "ot"        && <OTForecast week={week} />}
        {tab === "noshow"    && <NoShowRisk />}
        {tab === "location"  && <LocationHeatmap />}
        {tab === "optimizer" && <LaborOptimizer week={week} />}
        {tab === "forecast"  && <RevenueForecast week={week} />}
        {tab === "ask"       && <AskAI week={week} />}
        {tab === "auto"      && <AutoSchedule week={week} />}
        {tab === "buddy"     && <BuddyPunch />}
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// #7 SUNDAY DIGEST
// ═══════════════════════════════════════════════════════════════

function SundayDigest({ week, tenant }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/sunday-digest?week_start=${week}`);
      setData(res.data);
    } catch (e) {
      toast.error("Digest generation failed — check LLM key");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { generate(); }, [week]);

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-amber-400/20 to-orange-500/10 border border-amber-400/30 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
              Owner Digest · Week of {week}
            </div>
            <h2 className="text-white text-2xl font-black">{tenant.name}</h2>
          </div>
          <Button size="sm" onClick={generate} disabled={loading} className="bg-amber-400 text-black hover:bg-amber-500" data-testid="digest-regen">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Regen</>}
          </Button>
        </div>

        {loading && !data ? (
          <div className="text-white/60 py-8 text-center flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Gemini is analyzing your week…
          </div>
        ) : data && (
          <>
            <p className="text-white/90 text-lg leading-relaxed">{data.summary}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
              <DigestBlock title="🏆 Wins" tone="green" items={data.wins} />
              <DigestBlock title="⚠️ Concerns" tone="red" items={data.concerns} />
              <DigestBlock title="💡 Recommendations" tone="amber" items={data.recommendations} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DigestBlock({ title, tone, items }) {
  const bg = {
    green: "bg-emerald-500/10 border-emerald-400/30 text-emerald-100",
    red:   "bg-red-500/10 border-red-400/30 text-red-100",
    amber: "bg-amber-500/10 border-amber-400/30 text-amber-100",
  }[tone];
  return (
    <div className={`border rounded-xl p-4 ${bg}`}>
      <div className="text-xs font-bold uppercase tracking-wider mb-2 opacity-80">{title}</div>
      <ul className="space-y-1.5 text-sm">
        {(items || []).map((s, i) => (
          <li key={i} className="leading-snug">
            {typeof s === "string" ? s : (s?.action || s?.recommendation || JSON.stringify(s))}
            {s?.dollar_impact && <span className="ml-1 text-white/80 font-bold">${s.dollar_impact}</span>}
          </li>
        ))}
        {!items?.length && <li className="opacity-50">—</li>}
      </ul>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// #5 DEPT P&L
// ═══════════════════════════════════════════════════════════════

function DeptPnL({ week }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/ai/dept-pnl?week_start=${week}`).then((r) => setData(r.data));
  }, [week]);

  if (!data) return <Skel />;
  const totalRev = data.totals?.revenue || 0;
  const totalLabor = data.totals?.labor_cost || 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <BigStat label="Revenue" value={`$${totalRev.toLocaleString()}`} icon={TrendingUp} />
        <BigStat label="Labor" value={`$${totalLabor.toLocaleString()}`} icon={DollarSign} />
        <BigStat label="Rev / $1 Labor" value={data.totals?.rev_per_labor_dollar ? `$${data.totals.rev_per_labor_dollar}` : "—"} icon={Zap} />
      </div>
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/10 text-white/70 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">Dept</th>
              <th className="text-right px-4 py-2">Revenue</th>
              <th className="text-right px-4 py-2">Labor $</th>
              <th className="text-right px-4 py-2">Hours</th>
              <th className="text-right px-4 py-2">Rev/$</th>
              <th className="text-left px-4 py-2">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {data.departments.map((d, i) => (
              <tr key={i} className="border-t border-white/5 text-white">
                <td className="px-4 py-2 font-bold">
                  <span className="mr-2">{{green:"🟢",yellow:"🟡",red:"🔴",gray:"⚪"}[d.signal] || "⚪"}</span>
                  {d.department}
                </td>
                <td className="px-4 py-2 text-right">${(d.revenue||0).toLocaleString()}</td>
                <td className="px-4 py-2 text-right">${(d.labor_cost||0).toLocaleString()}</td>
                <td className="px-4 py-2 text-right">{d.labor_hours}</td>
                <td className="px-4 py-2 text-right font-bold">{d.rev_per_labor_dollar ?? "—"}</td>
                <td className="px-4 py-2 text-white/70 text-xs">{d.verdict}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// #2 OT FORECAST
// ═══════════════════════════════════════════════════════════════

function OTForecast({ week }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/ai/ot-forecast?week_start=${week}`).then((r) => setData(r.data));
  }, [week]);
  if (!data) return <Skel />;
  return (
    <div className="space-y-4">
      <BigStat label={`Projected OT cost this week (${data.at_risk.length} employees)`}
               value={`$${(data.total_projected_ot_cost||0).toLocaleString()}`}
               icon={AlertTriangle} tone="red" />
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/10 text-white/70 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Employee</th>
              <th className="text-right px-4 py-2">Dept</th>
              <th className="text-right px-4 py-2">Worked</th>
              <th className="text-right px-4 py-2">Remaining</th>
              <th className="text-right px-4 py-2">Proj Total</th>
              <th className="text-right px-4 py-2">OT $</th>
            </tr>
          </thead>
          <tbody>
            {data.at_risk.map((r, i) => (
              <tr key={i} className="border-t border-white/5 text-white">
                <td className="px-4 py-2 font-bold">{r.name} <span className="text-white/40 text-xs">#{r.code}</span></td>
                <td className="px-4 py-2 text-right text-white/70">{r.department}</td>
                <td className="px-4 py-2 text-right">{r.hours_worked}h</td>
                <td className="px-4 py-2 text-right">{r.hours_remaining}h</td>
                <td className={`px-4 py-2 text-right font-bold ${r.severity==="high"?"text-red-400":r.severity==="medium"?"text-amber-300":"text-white"}`}>
                  {r.projected_total}h
                </td>
                <td className="px-4 py-2 text-right font-bold text-amber-300">${r.projected_ot_cost}</td>
              </tr>
            ))}
            {!data.at_risk.length && (
              <tr><td colSpan={6} className="text-center py-6 text-white/60">No employees projected to exceed 40h this week 🎉</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// #6 NO-SHOW RISK
// ═══════════════════════════════════════════════════════════════

function NoShowRisk() {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/ai/noshow-risk`).then((r) => setData(r.data)); }, []);
  if (!data) return <Skel />;
  const risky = data.employees.filter(e => e.risk !== "low");
  return (
    <div className="space-y-4">
      <BigStat label={`${risky.length} employees flagged over last ${data.window_days} days`}
               value={`${data.employees.length} tracked`} icon={UserX} />
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/10 text-white/70 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Employee</th>
              <th className="text-right px-4 py-2">Dept</th>
              <th className="text-right px-4 py-2">Scheduled</th>
              <th className="text-right px-4 py-2">No-Shows</th>
              <th className="text-right px-4 py-2">Rate</th>
              <th className="text-right px-4 py-2">Risk</th>
            </tr>
          </thead>
          <tbody>
            {data.employees.slice(0, 25).map((r, i) => (
              <tr key={i} className="border-t border-white/5 text-white">
                <td className="px-4 py-2 font-bold">{r.name}</td>
                <td className="px-4 py-2 text-right text-white/70">{r.department}</td>
                <td className="px-4 py-2 text-right">{r.scheduled_shifts}</td>
                <td className="px-4 py-2 text-right">{r.no_shows}</td>
                <td className="px-4 py-2 text-right font-bold">{r.rate_pct}%</td>
                <td className="px-4 py-2 text-right">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                    r.risk==="high"?"bg-red-500/20 text-red-300":
                    r.risk==="medium"?"bg-amber-500/20 text-amber-300":
                    "bg-emerald-500/20 text-emerald-300"
                  }`}>{r.risk}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// #8 LOCATION HEATMAP
// ═══════════════════════════════════════════════════════════════

function LocationHeatmap() {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/ai/location-heatmap?days=30`).then((r) => setData(r.data)); }, []);
  if (!data) return <Skel />;
  return (
    <div className="space-y-4">
      <BigStat label={`${data.total_violations} violations in last ${data.window_days} days`}
               value={`${data.employees_flagged} employees`} icon={MapPin} tone="amber" />
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {data.ranking.length === 0 ? (
          <div className="text-center py-8 text-white/60">Nobody's punched from outside the geofence 🎉</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/10 text-white/70 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Employee</th>
                <th className="text-right px-4 py-2">Violations</th>
                <th className="text-right px-4 py-2">Max Distance</th>
                <th className="text-right px-4 py-2">Last Violation</th>
                <th className="text-right px-4 py-2">Severity</th>
              </tr>
            </thead>
            <tbody>
              {data.ranking.map((r, i) => (
                <tr key={i} className="border-t border-white/5 text-white">
                  <td className="px-4 py-2 font-bold">{r.name}</td>
                  <td className="px-4 py-2 text-right">{r.count}</td>
                  <td className="px-4 py-2 text-right">{r.max_distance_mi} mi</td>
                  <td className="px-4 py-2 text-right text-xs text-white/60">{r.last_at?.slice(0, 16)}</td>
                  <td className="px-4 py-2 text-right">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      r.severity==="high"?"bg-red-500/20 text-red-300":
                      r.severity==="medium"?"bg-amber-500/20 text-amber-300":
                      "bg-emerald-500/20 text-emerald-300"
                    }`}>{r.severity}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// #1 LABOR OPTIMIZER (AI)
// ═══════════════════════════════════════════════════════════════

function LaborOptimizer({ week }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/labor-optimizer?week_start=${week}`);
      setData(res.data);
    } catch (e) { toast.error("Optimizer failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-4">
      <Card className="bg-white/5 border-white/10 p-6 text-center">
        <Zap className="w-8 h-8 text-amber-300 mx-auto mb-2" />
        <p className="text-white/70 mb-3">
          AI analyzes 4 weeks of P&L history + this week's schedule<br />and proposes 3-5 concrete shift adjustments.
        </p>
        <Button onClick={run} disabled={loading} className="bg-amber-400 text-black hover:bg-amber-500">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing…</> : "Run Optimizer"}
        </Button>
      </Card>
      {data?.recommendations?.map((r, i) => (
        <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between mb-1">
            <div className="font-bold text-lg">
              {r.direction === "cut" ? <TrendingDown className="inline w-4 h-4 text-emerald-400 mr-1" /> : <TrendingUp className="inline w-4 h-4 text-amber-400 mr-1" />}
              {r.department} · {r.day} · {r.time_range}
            </div>
            <span className={`font-black ${r.direction==="cut"?"text-emerald-400":"text-amber-400"}`}>
              {r.direction==="cut" ? "-" : "+"}{r.hours}h · ${Math.abs(r.dollar_impact||0)}
            </span>
          </div>
          <p className="text-white/70 text-sm">{r.reason}</p>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// #4 REVENUE FORECAST (AI)
// ═══════════════════════════════════════════════════════════════

function RevenueForecast({ week }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ai/revenue-forecast?target_date=${week}`);
      setData(res.data);
    } catch (e) { toast.error("Forecast failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-4">
      <Card className="bg-white/5 border-white/10 p-6 text-center">
        <TrendingUp className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
        <p className="text-white/70 mb-3">AI forecasts next week's revenue from historical POS reports.</p>
        <Button onClick={run} disabled={loading} className="bg-emerald-400 text-black hover:bg-emerald-500">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Forecasting…</> : "Run Forecast"}
        </Button>
      </Card>
      {data?.projection && (
        <Card className="bg-white/5 border-white/10 p-6 text-white">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-[10px] uppercase text-white/60">Low</div>
              <div className="text-2xl font-black">${data.projection.net_sales_low?.toLocaleString()}</div>
            </div>
            <div className="border-x border-white/10">
              <div className="text-[10px] uppercase text-amber-300">Most Likely</div>
              <div className="text-3xl font-black text-amber-300">${data.projection.net_sales_mid?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-white/60">High</div>
              <div className="text-2xl font-black">${data.projection.net_sales_high?.toLocaleString()}</div>
            </div>
          </div>
          <p className="text-white/70 text-sm mt-4 border-t border-white/10 pt-3">{data.projection.reasoning}</p>
          <div className="text-[10px] uppercase text-white/50 mt-2">Confidence: {data.projection.confidence}</div>
        </Card>
      )}
      {data?.note && <p className="text-white/50 text-sm text-center">{data.note}</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// #10 ASK AI ANYTHING
// ═══════════════════════════════════════════════════════════════

function AskAI({ week }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const ask = async () => {
    if (!input.trim()) return;
    const q = input;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/ask`, { question: q, context_week: week });
      setMsgs((m) => [...m, { role: "ai", text: res.data.answer }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "ai", text: "Sorry — LLM call failed." }]);
    } finally {
      setLoading(false);
    }
  };
  const suggested = [
    "Which department has the worst labor efficiency this week?",
    "Who's most likely to hit overtime and how do I stop it?",
    "Which cashiers are chronically late?",
    "Show me the biggest wins this month",
  ];
  return (
    <div className="space-y-3">
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 min-h-[300px] max-h-[500px] overflow-y-auto space-y-3">
        {msgs.length === 0 && (
          <div className="text-white/60 text-center py-8">
            <MessageSquare className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p>Ask any business question. AI reads your live data.</p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {suggested.map((s, i) => (
                <button key={i} onClick={() => setInput(s)}
                  className="text-xs px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white/80">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
            <div className={`inline-block max-w-[85%] rounded-2xl px-4 py-2 ${
              m.role === "user" ? "bg-amber-400 text-black" : "bg-white/10 text-white whitespace-pre-wrap"
            }`}>{m.text}</div>
          </div>
        ))}
        {loading && <div className="text-white/60 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Thinking…</div>}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="Ask about labor, OT, no-shows, revenue, employees…"
          data-testid="ask-input"
          className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white text-sm placeholder:text-white/40" />
        <Button onClick={ask} disabled={loading} className="bg-amber-400 text-black hover:bg-amber-500" data-testid="ask-send">
          Send
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// #9 AUTO-SCHEDULE
// ═══════════════════════════════════════════════════════════════

function AutoSchedule({ week }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/auto-schedule?week_start=${week}`);
      setData(res.data);
    } catch (e) { toast.error("Auto-schedule failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-4">
      <Card className="bg-white/5 border-white/10 p-6 text-center">
        <Calendar className="w-8 h-8 text-blue-300 mx-auto mb-2" />
        <p className="text-white/70 mb-3">Generate a draft schedule for {week} based on last week's patterns.</p>
        <Button onClick={run} disabled={loading} className="bg-blue-400 text-black hover:bg-blue-500">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</> : "Generate Draft"}
        </Button>
      </Card>
      {data && (
        <Card className="bg-white/5 border-white/10 p-4 text-white">
          <div className="mb-3 flex items-center justify-between">
            <div className="font-bold">{data.proposed_shifts?.length || 0} proposed shifts</div>
            <div className="text-xs text-white/60">Based on week of {data.based_on}</div>
          </div>
          <p className="text-white/60 text-sm">{data.note}</p>
          <p className="text-xs text-amber-300 mt-3">Preview only — apply from the Schedule page.</p>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// #3 BUDDY-PUNCH
// ═══════════════════════════════════════════════════════════════

function BuddyPunch() {
  const [data, setData] = useState(null);
  useEffect(() => { axios.post(`${API}/ai/buddy-punch/scan?days=7`).then((r) => setData(r.data)); }, []);
  if (!data) return <Skel />;
  return (
    <div className="space-y-4">
      <BigStat label={`${data.punches_reviewed} punches reviewed this week`}
               value={`${data.flagged?.length || 0} flagged`}
               icon={AlertTriangle} tone={data.flagged?.length ? "red" : "green"} />
      <Card className="bg-white/5 border-white/10 p-4 text-white text-sm">{data.note}</Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Shared UI
// ═══════════════════════════════════════════════════════════════

function BigStat({ label, value, icon: Icon, tone = "neutral" }) {
  const bg = {
    neutral: "from-white/10 to-white/5 border-white/10 text-white",
    green:   "from-emerald-500/20 to-emerald-500/5 border-emerald-400/30 text-emerald-100",
    red:     "from-red-500/20 to-red-500/5 border-red-400/30 text-red-100",
    amber:   "from-amber-500/20 to-amber-500/5 border-amber-400/30 text-amber-100",
  }[tone];
  return (
    <Card className={`bg-gradient-to-br border rounded-2xl p-5 ${bg}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-6 h-6 opacity-70" />
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">{label}</div>
          <div className="text-2xl font-black mt-0.5">{value}</div>
        </div>
      </div>
    </Card>
  );
}

function Skel() {
  return <div className="h-40 bg-white/5 rounded-xl animate-pulse" />;
}
